import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { serviceAPI, categoryAPI } from '../utils/api';
import ServiceCard from '../components/common/ServiceCard';
import './Browse.css';

const SORT_OPTIONS = [
  { value: 'createdAt-desc', label: 'Newest First' },
  { value: 'createdAt-asc', label: 'Oldest First' },
  { value: 'price-asc', label: 'Price: Low to High' },
  { value: 'price-desc', label: 'Price: High to Low' },
  { value: 'avgRating-desc', label: 'Highest Rated' },
];

export default function Browse() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1, page: 1 });
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    category: searchParams.get('category') || '',
    city: searchParams.get('city') || '',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
    sort: 'createdAt-desc',
    page: 1,
  });

  const [searchInput, setSearchInput] = useState(filters.search);

  useEffect(() => {
    categoryAPI.getAll().then(r => setCategories(r.data)).catch(() => {});
  }, []);

  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const [sortBy, sortOrder] = filters.sort.split('-');
      const params = {
        page: filters.page,
        limit: 12,
        sortBy,
        sortOrder,
      };
      if (filters.search) params.search = filters.search;
      if (filters.category) params.category = filters.category;
      if (filters.city) params.city = filters.city;
      if (filters.minPrice) params.minPrice = filters.minPrice;
      if (filters.maxPrice) params.maxPrice = filters.maxPrice;

      const { data } = await serviceAPI.search(params);
      setServices(data.services);
      setPagination(data.pagination);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  const updateFilter = (key, value) => {
    setFilters(f => ({ ...f, [key]: value, page: 1 }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    updateFilter('search', searchInput);
  };

  const clearFilters = () => {
    setFilters({ search: '', category: '', city: '', minPrice: '', maxPrice: '', sort: 'createdAt-desc', page: 1 });
    setSearchInput('');
    setSearchParams({});
  };

  const hasFilters = filters.search || filters.category || filters.city || filters.minPrice || filters.maxPrice;

  return (
    <div className="browse-page">
      {/* Search bar */}
      <div className="browse-header">
        <div className="container browse-header-inner">
          <div className="browse-header-title">🔍 Browse Services</div>
          <form onSubmit={handleSearch} className="browse-search">
            <div className="search-bar browse-search-bar">
              <input
                type="text"
                placeholder="Search for food catering, braiding, tailoring..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
              />
              <button type="submit">Search</button>
            </div>
          </form>
        </div>
      </div>

      <div className="container browse-body">
        {/* Sidebar Filters */}
        <aside className="browse-filters">
          <div className="filter-header">
            <h3>Filters</h3>
            {hasFilters && <button className="clear-filters" onClick={clearFilters}>Clear All</button>}
          </div>

          <div className="filter-group">
            <label className="filter-label">Category</label>
            <div className="filter-options">
              <button
                className={`filter-option ${!filters.category ? 'active' : ''}`}
                onClick={() => updateFilter('category', '')}
              >All Categories</button>
              {categories.map(c => (
                <button
                  key={c.id}
                  className={`filter-option ${filters.category === c.slug ? 'active' : ''}`}
                  onClick={() => updateFilter('category', c.slug)}
                >{c.icon} {c.name}</button>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">City</label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g. Houston"
              value={filters.city}
              onChange={e => updateFilter('city', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">Price Range</label>
            <div className="price-range">
              <input type="number" className="form-input" placeholder="Min $" value={filters.minPrice}
                onChange={e => updateFilter('minPrice', e.target.value)} min="0" />
              <span>–</span>
              <input type="number" className="form-input" placeholder="Max $" value={filters.maxPrice}
                onChange={e => updateFilter('maxPrice', e.target.value)} min="0" />
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="browse-main">
          {/* Top bar */}
          <div className="browse-topbar">
            <div className="browse-count">
              {loading ? 'Loading...' : `${pagination.total} service${pagination.total !== 1 ? 's' : ''} found`}
              {filters.search && <span className="browse-query"> for "{filters.search}"</span>}
            </div>
            <select
              className="form-select sort-select"
              value={filters.sort}
              onChange={e => updateFilter('sort', e.target.value)}
            >
              {SORT_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Active filter chips */}
          {hasFilters && (
            <div className="filter-chips">
              {filters.search && <span className="chip">Search: {filters.search} <button onClick={() => { updateFilter('search', ''); setSearchInput(''); }}>×</button></span>}
              {filters.category && <span className="chip">Category: {categories.find(c => c.slug === filters.category)?.name || filters.category} <button onClick={() => updateFilter('category', '')}>×</button></span>}
              {filters.city && <span className="chip">City: {filters.city} <button onClick={() => updateFilter('city', '')}>×</button></span>}
              {(filters.minPrice || filters.maxPrice) && (
                <span className="chip">
                  Price: ${filters.minPrice || '0'} – ${filters.maxPrice || '∞'}
                  <button onClick={() => { updateFilter('minPrice', ''); updateFilter('maxPrice', ''); }}>×</button>
                </span>
              )}
            </div>
          )}

          {/* Grid */}
          {loading ? (
            <div className="loading-page"><div className="spinner" /></div>
          ) : services.length > 0 ? (
            <div className="grid grid-3">
              {services.map(s => <ServiceCard key={s.id} service={s} />)}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">🔍</div>
              <h3>No services found</h3>
              <p>Try adjusting your filters or search terms</p>
              <button className="btn btn-outline" onClick={clearFilters}>Clear Filters</button>
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button disabled={filters.page <= 1} onClick={() => updateFilter('page', filters.page - 1)}>← Prev</button>
              {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} className={filters.page === p ? 'active' : ''} onClick={() => updateFilter('page', p)}>{p}</button>
              ))}
              <button disabled={filters.page >= pagination.totalPages} onClick={() => updateFilter('page', filters.page + 1)}>Next →</button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
